// src/services/paymentService.js
import { httpsCallable } from 'firebase/functions';
import { 
  collection, 
  addDoc, 
  serverTimestamp,
  doc,
  updateDoc 
} from 'firebase/firestore';
import { functions, db, COLLECTIONS } from './firebase';

export class PaymentService {
  // Calculate amount based on plan and quantity
  static calculateAmount(plan, quantity = 1, learnerCount = 0) {
    let baseAmount = 0;
    
    if (plan.type === 'school') {
      // School plans are per user
      baseAmount = (plan.discountedPrice || plan.price) * quantity;
    } else if (plan.type === 'addon') {
      // AI Reports addon is per learner
      baseAmount = plan.price * learnerCount;
    } else {
      // Individual plans are flat rate
      baseAmount = plan.discountedPrice || plan.price;
    }
    
    return baseAmount;
  }

  // Create manual payment (direct bank transfer)
  static async createManualPayment(subscriptionData) {
    try {
      // Create subscription with pending status
      const subscriptionRef = await addDoc(collection(db, COLLECTIONS.SUBSCRIPTIONS), {
        ...subscriptionData,
        status: 'pending_confirmation',
        paymentMethod: 'manual',
        accessUntil: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000), // 7 days access
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        startDate: new Date(),
        endDate: new Date(Date.now() + (subscriptionData.planId.includes('annual') ? 365 : 120) * 24 * 60 * 60 * 1000)
      });

      // Create pending payment record
      await addDoc(collection(db, COLLECTIONS.PAYMENTS), {
        subscriptionId: subscriptionRef.id,
        userId: subscriptionData.userId,
        amount: subscriptionData.totalAmount,
        paymentMethod: 'manual',
        status: 'pending_confirmation',
        paymentDetails: subscriptionData.paymentDetails,
        createdAt: serverTimestamp(),
        expectedDate: new Date(subscriptionData.paymentDetails.depositDate)
      });

      return {
        success: true,
        subscriptionId: subscriptionRef.id,
        message: 'Manual payment registered successfully'
      };
    } catch (error) {
      console.error('Error creating manual payment:', error);
      throw new Error(error.message || 'Failed to create manual payment');
    }
  }

  // Confirm manual payment (admin function)
  static async confirmManualPayment(subscriptionId, confirmed = true) {
    try {
      const subscriptionRef = doc(db, COLLECTIONS.SUBSCRIPTIONS, subscriptionId);
      
      const updateData = {
        status: confirmed ? 'active' : 'failed',
        updatedAt: serverTimestamp()
      };

      if (confirmed) {
        updateData.activatedAt = serverTimestamp();
        // Extend access beyond the 7-day pending period
        updateData.accessUntil = new Date(Date.now() + 365 * 24 * 60 * 60 * 1000);
      }

      await updateDoc(subscriptionRef, updateData);

      return {
        success: true,
        message: confirmed ? 'Payment confirmed and subscription activated' : 'Payment marked as failed'
      };
    } catch (error) {
      console.error('Error confirming manual payment:', error);
      throw error;
    }
  }

  // Check if subscription has temporary access
  static hasTemporaryAccess(subscription) {
    if (!subscription) return false;
    
    if (subscription.status === 'pending_confirmation' && subscription.accessUntil) {
      return new Date() < new Date(subscription.accessUntil);
    }
    
    return subscription.status === 'active';
  }

  // Get remaining temporary access time
  static getRemainingAccessTime(subscription) {
    if (!subscription || !subscription.accessUntil) return null;
    
    const now = new Date();
    const accessUntil = new Date(subscription.accessUntil);
    const remaining = accessUntil - now;
    
    if (remaining <= 0) return null;
    
    const days = Math.floor(remaining / (1000 * 60 * 60 * 24));
    const hours = Math.floor((remaining % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    
    return { days, hours, total: remaining };
  }

  // Create payment using Cloud Function (PayFast)
  static async createPayment(subscriptionData, userProfile) {
    try {
      const createPaymentFunction = httpsCallable(functions, 'createPayment');
      
      const response = await createPaymentFunction({
        ...subscriptionData,
        userProfile
      });

      return response.data;
    } catch (error) {
      console.error('Error creating payment:', error);
      throw new Error(error.message || 'Failed to create payment');
    }
  }

  // Process payment (redirect to PayFast)
  static processPayment(paymentData, payfastUrl) {
    const form = document.createElement('form');
    form.method = 'POST';
    form.action = payfastUrl;
    
    // Add all payment data as hidden fields
    Object.keys(paymentData).forEach(key => {
      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = key;
      input.value = paymentData[key];
      form.appendChild(input);
    });
    
    document.body.appendChild(form);
    form.submit();
  }

  // Handle payment return (success/cancel)
  static async handlePaymentReturn(params, isSuccess = true) {
    try {
      const endpoint = isSuccess ? 'handlePaymentSuccess' : 'handlePaymentCancel';
      
      const response = await fetch(
        `${process.env.REACT_APP_FUNCTIONS_URL}/${endpoint}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(params)
        }
      );

      if (!response.ok) {
        throw new Error('Failed to process payment return');
      }

      return await response.json();
    } catch (error) {
      console.error('Error handling payment return:', error);
      throw error;
    }
  }

  // Get user subscriptions
  static async getUserSubscriptions(userId) {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_FUNCTIONS_URL}/getSubscriptions?userId=${userId}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch subscriptions');
      }

      const data = await response.json();
      return data.subscriptions;
    } catch (error) {
      console.error('Error getting subscriptions:', error);
      throw error;
    }
  }

  // Cancel subscription
  static async cancelSubscription(subscriptionId, userId, reason) {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_FUNCTIONS_URL}/cancelSubscription`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            subscriptionId,
            userId,
            reason
          })
        }
      );

      if (!response.ok) {
        throw new Error('Failed to cancel subscription');
      }

      return await response.json();
    } catch (error) {
      console.error('Error cancelling subscription:', error);
      throw error;
    }
  }

  // Get AI reports usage
  static async getAIReportsUsage(userId) {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_FUNCTIONS_URL}/getAIReportsUsage?userId=${userId}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch AI reports usage');
      }

      const data = await response.json();
      return data.usage;
    } catch (error) {
      console.error('Error getting AI reports usage:', error);
      throw error;
    }
  }

  // Handle payment cancellation
  static async handlePaymentCancel(subscriptionId, userId) {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_FUNCTIONS_URL}/handlePaymentCancel`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            subscriptionId,
            userId
          })
        }
      );

      if (!response.ok) {
        throw new Error('Failed to handle payment cancellation');
      }

      return await response.json();
    } catch (error) {
      console.error('Error handling payment cancellation:', error);
      throw error;
    }
  }

  // Validate payment signature (for webhook validation if needed on frontend)
  static validateAmount(plan, quantity, learnerCount, receivedAmount) {
    const calculatedAmount = this.calculateAmount(plan, quantity, learnerCount);
    return Math.abs(calculatedAmount - receivedAmount) < 0.01; // Allow for floating point precision
  }

  // Format currency for display
  static formatCurrency(amount) {
    return new Intl.NumberFormat('en-ZA', {
      style: 'currency',
      currency: 'ZAR',
      minimumFractionDigits: 2
    }).format(amount);
  }

  // Get payment status message
  static getPaymentStatusMessage(status) {
    const statusMessages = {
      'pending': 'Payment is being processed',
      'pending_confirmation': 'Awaiting payment confirmation',
      'active': 'Payment successful - subscription active',
      'failed': 'Payment failed - please try again',
      'cancelled': 'Payment was cancelled',
      'expired': 'Payment link has expired'
    };
    
    return statusMessages[status] || 'Unknown payment status';
  }

  // Calculate savings for early bird
  static calculateSavings(originalPrice, discountedPrice) {
    if (!discountedPrice || !originalPrice) return 0;
    return originalPrice - discountedPrice;
  }

  // Get discount percentage
  static getDiscountPercentage(originalPrice, discountedPrice) {
    if (!discountedPrice || !originalPrice) return 0;
    return Math.round(((originalPrice - discountedPrice) / originalPrice) * 100);
  }
}